import * as React from 'react';
import {
  getMeta,
  getSystem,
  TypeImplementation,
  Types,
  type Type,
  withMeta,
  withSystem,
} from '@packages/systems/wfdl';
import {
  enumOf,
  TEnum,
  TTypeApplication,
  TTypeConstructor,
  TTypeReference,
} from '@packages/systems/wfdl-ast';
import {
  ContextualExpressions,
  type ContextualExpression,
  getAnn,
  stripAnn,
  updateAnn,
} from '@packages/utilities/contextual-expression';
import {ExpressionEditor} from '@packages/systems/ExpressionEditor-utils';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {pluginName} from '../constants';

const dateFormatType = TTypeConstructor(
  qualify(pluginName, typeName('DateFormat'))
);

export const TDateFormat = (row: Type): Type =>
  TTypeApplication({con: dateFormatType, arg: row});

function getKnownValues(row: Type): Record<string, string> {
  return Object.fromEntries(
    Object.entries(Types.deconstructRow(row).items).map(([label, type]) => [
      label,
      getSystem(type)?.label || label,
    ])
  );
}

export function getRenderableDateFormatType(
  type: Type,
  currentValue: string | null
): Type {
  const enumValues = Types.getEnumValues(type);
  const typeApplication = Types.deconstructTypeApplication(type);
  const row = typeApplication?.args.at(-1);
  const knownValues = enumValues || (row ? getKnownValues(row) : {});
  const values =
    currentValue !== null &&
    currentValue !== '' &&
    !Object.prototype.hasOwnProperty.call(knownValues, currentValue)
      ? {...knownValues, [currentValue]: currentValue}
      : knownValues;

  let renderableType = withMeta(
    {...getMeta(type), variant: 'menu'},
    TEnum(values)
  );
  const system = getSystem(type);
  if (system) {
    renderableType = withSystem(system, renderableType);
  }
  return renderableType;
}

type DateFormatRenderProps = {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
};

function DateFormatRender({expr, dispatch}: DateFormatRenderProps) {
  const annotation = getAnn(expr);
  if (!annotation?.type) {
    return null;
  }

  const currentValue = ContextualExpressions.getEnum(stripAnn(expr));
  const renderableType = getRenderableDateFormatType(
    annotation.type,
    currentValue
  );
  const renderableExpr = updateAnn(
    (currentAnnotation) => ({
      ...currentAnnotation,
      type: renderableType,
    }),
    expr
  );

  return <ExpressionEditor expression={renderableExpr} dispatch={dispatch} />;
}

export const DateFormatType = TypeImplementation.Alias({
  params: ['R'],
  type: enumOf(TTypeReference('R')),
  render: DateFormatRender,
  renderSummary: ({expr}) => (
    <span>{ContextualExpressions.getEnum(stripAnn(expr))}</span>
  ),
});
